import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { palette } from '../../../theme/paletteV2.ts';
import { BAR_RADIUS, X_AXIS_HEIGHT } from '../constants.tsx';
import type { PyramidDiagramRow, PyramidDiagramValueKey } from '../types.ts';
import { TooltipContent } from './TooltipContent.tsx';

type SideChartProps = {
  side: 'left' | 'right';
  data: PyramidDiagramRow[];
  dataKey: PyramidDiagramValueKey;
  name: string;
  color: string;
  domainMax: number;
  ticks: number[];
  valueFormatter: (value: number) => string;
  isAnimationActive: boolean;
  height: number;
  leftSeries: { label: string; color: string };
  rightSeries: { label: string; color: string };
};

export const SideChart = ({
  side,
  data,
  dataKey,
  name,
  color,
  domainMax,
  ticks,
  valueFormatter,
  isAnimationActive,
  height,
  leftSeries,
  rightSeries,
}: SideChartProps) => {
  const isLeft = side === 'left';

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        layout="vertical"
        data={data}
        margin={{ top: 4, right: isLeft ? 4 : 8, bottom: 0, left: isLeft ? 8 : 4 }}
        barCategoryGap="10%"
      >
        <CartesianGrid horizontal={false} vertical={true} stroke={palette.divider} syncWithTicks={true} />
        <XAxis
          type="number"
          domain={[0, domainMax]}
          ticks={ticks}
          reversed={isLeft}
          tickFormatter={valueFormatter}
          axisLine={false}
          tickLine={false}
          height={X_AXIS_HEIGHT}
          tick={{ fill: palette.text.secondary, fontSize: 12 }}
        />
        <YAxis type="category" dataKey="label" hide={true} />
        <Tooltip
          cursor={{ fill: palette.background.boxLight, opacity: 0.6 }}
          content={
            <TooltipContent
              valueFormatter={valueFormatter}
              left={{ ...leftSeries, dataKey: 'leftValue' }}
              right={{ ...rightSeries, dataKey: 'rightValue' }}
            />
          }
          // Default position animation slides the tooltip in from the chart corner on first hover
          isAnimationActive={false}
        />
        <Bar
          dataKey={dataKey}
          name={name}
          fill={color}
          // Round only the outer tip; corner order follows the bar's drawing direction, so it holds for the reversed left axis too
          radius={[0, BAR_RADIUS, BAR_RADIUS, 0]}
          isAnimationActive={isAnimationActive}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};
